import type { VisualizationStep } from '../types'

export interface BarArrayState {
  array: number[]
  comparing: [number, number] | null
  swapped: boolean
  // Bubble sort: indices >= sortedFromIndex are in final position (sorted suffix)
  sortedFromIndex: number
  // Selection/insertion sort: indices <= sortedToIndex are in final position (sorted prefix), -1 = none
  sortedToIndex?: number
  // Selection/insertion sort / heap: index of the current tracked element
  minIndex?: number
  // Merge sort: inclusive [l, r] range of the active merge window
  mergeRegion?: [number, number]
  // Quick sort / quickselect: current pivot index (cyan)
  pivotIndex?: number
  // Quick sort / quickselect: indices confirmed in their final global position (green)
  finalizedIndices?: number[]
  // Binary search: active search window [left, right]; bars outside are dimmed
  searchRange?: [number, number]
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

export class BarArrayVisualizer {
  private readonly canvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!
  }

  static generateBubbleSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    let sortedFromIndex = n

    steps.push({
      index: idx++,
      state: { array: [...a], comparing: null, swapped: false, sortedFromIndex },
      highlightedIndices: [],
      message: 'Ready — press Play to start.',
      isComparison: false,
      isSwap: false,
    })

    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < n - 1 - i; j++) {
        const v1 = a[j]
        const v2 = a[j + 1]
        steps.push({
          index: idx++,
          state: { array: [...a], comparing: [j, j + 1], swapped: false, sortedFromIndex },
          highlightedIndices: [j, j + 1],
          message: `Comparing ${v1} and ${v2}`,
          isComparison: true,
          isSwap: false,
        })
        if (v1 > v2) {
          ;[a[j], a[j + 1]] = [a[j + 1], a[j]]
          steps.push({
            index: idx++,
            state: { array: [...a], comparing: [j, j + 1], swapped: true, sortedFromIndex },
            highlightedIndices: [j, j + 1],
            message: `${v1} > ${v2} — swapping`,
            isComparison: false,
            isSwap: true,
          })
        }
      }
      sortedFromIndex = n - 1 - i
    }

    steps.push({
      index: idx++,
      state: { array: [...a], comparing: null, swapped: false, sortedFromIndex: 0 },
      highlightedIndices: [],
      message: 'Sorted! O(n²) comparisons complete.',
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateSelectionSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    let sortedToIndex = -1

    const state = (): Omit<BarArrayState, 'comparing' | 'minIndex'> => ({
      array: [...a],
      swapped: false,
      sortedFromIndex: n,
      sortedToIndex,
    })

    steps.push({
      index: idx++,
      state: { ...state(), comparing: null },
      highlightedIndices: [],
      message: 'Ready — press Play to start.',
      isComparison: false,
      isSwap: false,
    })

    for (let i = 0; i < n - 1; i++) {
      let minIdx = i

      for (let j = i + 1; j < n; j++) {
        steps.push({
          index: idx++,
          state: { ...state(), comparing: [j, minIdx], minIndex: minIdx },
          highlightedIndices: [j, minIdx],
          message: `Pass ${i + 1}: is ${a[j]} < current min ${a[minIdx]}?`,
          isComparison: true,
          isSwap: false,
        })

        if (a[j] < a[minIdx]) {
          minIdx = j
          steps.push({
            index: idx++,
            state: { ...state(), comparing: null, minIndex: minIdx },
            highlightedIndices: [minIdx],
            message: `New minimum found: ${a[minIdx]} at position ${minIdx}`,
            isComparison: false,
            isSwap: false,
          })
        }
      }

      if (minIdx !== i) {
        steps.push({
          index: idx++,
          state: { ...state(), comparing: [i, minIdx], swapped: true },
          highlightedIndices: [i, minIdx],
          message: `Placing ${a[minIdx]} at position ${i} (swap #${i + 1})`,
          isComparison: false,
          isSwap: true,
        })
        ;[a[i], a[minIdx]] = [a[minIdx], a[i]]
      }

      sortedToIndex = i
      steps.push({
        index: idx++,
        state: { ...state(), comparing: null },
        highlightedIndices: [i],
        message: `${a[i]} is in its final position.`,
        isComparison: false,
        isSwap: false,
      })
    }

    steps.push({
      index: idx++,
      state: { ...state(), comparing: null, sortedToIndex: n - 1 },
      highlightedIndices: [],
      message: 'Sorted! At most n−1 swaps — minimum writes.',
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateInsertionSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    let sortedToIndex = 0

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex: n,
      sortedToIndex,
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Ready — press Play to start.',
      isComparison: false,
      isSwap: false,
    })

    for (let i = 1; i < n; i++) {
      const key = a[i]

      steps.push({
        index: idx++,
        state: state({ minIndex: i }),
        highlightedIndices: [i],
        message: `Picking up ${key} to insert into sorted portion`,
        isComparison: false,
        isSwap: false,
      })

      let j = i - 1
      while (j >= 0 && a[j] > key) {
        steps.push({
          index: idx++,
          state: state({ comparing: [j, j + 1], minIndex: j + 1 }),
          highlightedIndices: [j, j + 1],
          message: `${a[j]} > ${key} — shift right to make room`,
          isComparison: true,
          isSwap: false,
        })

        a[j + 1] = a[j]

        steps.push({
          index: idx++,
          state: state({ comparing: [j, j + 1], swapped: true, minIndex: j }),
          highlightedIndices: [j, j + 1],
          message: `Shifted ${a[j + 1]} right — gap at position ${j}`,
          isComparison: false,
          isSwap: true,
        })

        j--
      }

      a[j + 1] = key
      sortedToIndex = i

      steps.push({
        index: idx++,
        state: state(),
        highlightedIndices: [j + 1],
        message: `${key} placed at position ${j + 1} ✓`,
        isComparison: false,
        isSwap: false,
      })
    }

    steps.push({
      index: idx++,
      state: state({ sortedToIndex: n - 1 }),
      highlightedIndices: [],
      message: 'Sorted! O(n) on nearly-sorted data.',
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateMergeSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex: n,
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Ready — press Play to start.',
      isComparison: false,
      isSwap: false,
    })

    for (let width = 1; width < n; width *= 2) {
      for (let l = 0; l < n; l += 2 * width) {
        const mid = Math.min(l + width, n)
        const r = Math.min(l + 2 * width, n)
        if (mid >= r) continue

        steps.push({
          index: idx++,
          state: state({ mergeRegion: [l, r - 1] }),
          highlightedIndices: Array.from({ length: r - l }, (_, k) => l + k),
          message: `Merging [${l}..${mid - 1}] and [${mid}..${r - 1}]`,
          isComparison: false,
          isSwap: false,
        })

        const temp: number[] = []
        let i = l
        let j = mid

        while (i < mid && j < r) {
          const v1 = a[i]
          const v2 = a[j]

          steps.push({
            index: idx++,
            state: state({ comparing: [i, j], mergeRegion: [l, r - 1] }),
            highlightedIndices: [i, j],
            message: `Comparing ${v1} and ${v2}`,
            isComparison: true,
            isSwap: false,
          })

          if (v1 <= v2) {
            temp.push(v1)
            steps.push({
              index: idx++,
              state: state({ comparing: [i, j], swapped: true, mergeRegion: [l, r - 1] }),
              highlightedIndices: [i],
              message: `${v1} ≤ ${v2} — taking ${v1} from left`,
              isComparison: false,
              isSwap: true,
            })
            i++
          } else {
            temp.push(v2)
            steps.push({
              index: idx++,
              state: state({ comparing: [i, j], swapped: true, mergeRegion: [l, r - 1] }),
              highlightedIndices: [j],
              message: `${v2} < ${v1} — taking ${v2} from right`,
              isComparison: false,
              isSwap: true,
            })
            j++
          }
        }

        while (i < mid) temp.push(a[i++])
        while (j < r) temp.push(a[j++])

        for (let k = 0; k < temp.length; k++) a[l + k] = temp[k]

        steps.push({
          index: idx++,
          state: state({ mergeRegion: [l, r - 1] }),
          highlightedIndices: Array.from({ length: r - l }, (_, k) => l + k),
          message: `Merged [${l}..${r - 1}] — ${r - l} elements locally sorted`,
          isComparison: false,
          isSwap: false,
        })
      }
    }

    steps.push({
      index: idx++,
      state: state({ sortedFromIndex: 0 }),
      highlightedIndices: [],
      message: 'Sorted! O(n log n) — divide and conquer wins.',
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateQuickSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    const finalized = new Set<number>()

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex: n,
      finalizedIndices: [...finalized],
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Ready — press Play to start.',
      isComparison: false,
      isSwap: false,
    })

    function partition(lo: number, hi: number): number {
      const pivotVal = a[hi]

      steps.push({
        index: idx++,
        state: state({ pivotIndex: hi, mergeRegion: [lo, hi] }),
        highlightedIndices: [hi],
        message: `Partition [${lo}..${hi}]: pivot = ${pivotVal}`,
        isComparison: false,
        isSwap: false,
      })

      let i = lo - 1
      for (let j = lo; j < hi; j++) {
        const v = a[j]

        steps.push({
          index: idx++,
          state: state({ comparing: [j, hi], pivotIndex: hi, mergeRegion: [lo, hi] }),
          highlightedIndices: [j, hi],
          message: `Is ${v} ≤ pivot (${pivotVal})?`,
          isComparison: true,
          isSwap: false,
        })

        if (v <= pivotVal) {
          i++
          if (i !== j) {
            ;[a[i], a[j]] = [a[j], a[i]]
            steps.push({
              index: idx++,
              state: state({ comparing: [i, j], swapped: true, pivotIndex: hi, mergeRegion: [lo, hi] }),
              highlightedIndices: [i, j],
              message: `${v} ≤ ${pivotVal} — swap into left partition`,
              isComparison: false,
              isSwap: true,
            })
          }
        }
      }

      const pivotPos = i + 1
      ;[a[pivotPos], a[hi]] = [a[hi], a[pivotPos]]
      finalized.add(pivotPos)

      steps.push({
        index: idx++,
        state: state({ comparing: [pivotPos, hi], swapped: true }),
        highlightedIndices: [pivotPos],
        message: `Pivot ${pivotVal} placed at final position ${pivotPos} ✓`,
        isComparison: false,
        isSwap: true,
      })

      return pivotPos
    }

    function qsort(lo: number, hi: number): void {
      if (lo >= hi) {
        if (lo === hi) finalized.add(lo)
        return
      }
      const p = partition(lo, hi)
      qsort(lo, p - 1)
      qsort(p + 1, hi)
    }

    qsort(0, n - 1)

    steps.push({
      index: idx++,
      state: state({ sortedFromIndex: 0 }),
      highlightedIndices: [],
      message: 'Sorted! O(n log n) average — in-place, no extra memory.',
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateQuickselectSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    // Find the element at the 75th percentile — the campaign score threshold
    const k = Math.floor(n * 0.75) - 1
    const finalized = new Set<number>()

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex: n,
      finalizedIndices: [...finalized],
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state({ minIndex: k }),
      highlightedIndices: [k],
      message: `Find the 75th-percentile score threshold (position ${k}) — no full sort needed`,
      isComparison: false,
      isSwap: false,
    })

    function partition(low: number, high: number): number {
      const pivot = a[high]

      steps.push({
        index: idx++,
        state: state({ pivotIndex: high, mergeRegion: [low, high] }),
        highlightedIndices: [high],
        message: `Pivot: ${pivot} — partition [${low}..${high}]`,
        isComparison: false,
        isSwap: false,
      })

      let i = low - 1
      for (let j = low; j < high; j++) {
        steps.push({
          index: idx++,
          state: state({ comparing: [j, high], pivotIndex: high, mergeRegion: [low, high] }),
          highlightedIndices: [j, high],
          message: `Is ${a[j]} ≤ pivot ${pivot}?`,
          isComparison: true,
          isSwap: false,
        })

        if (a[j] <= pivot) {
          i++
          if (i !== j) {
            ;[a[i], a[j]] = [a[j], a[i]]
            steps.push({
              index: idx++,
              state: state({ comparing: [i, j], swapped: true, pivotIndex: high, mergeRegion: [low, high] }),
              highlightedIndices: [i, j],
              message: `${a[j]} ≤ ${pivot} — move into left partition`,
              isComparison: false,
              isSwap: true,
            })
          }
        }
      }

      ;[a[i + 1], a[high]] = [a[high], a[i + 1]]
      const pivotPos = i + 1
      finalized.add(pivotPos)

      steps.push({
        index: idx++,
        state: state({ pivotIndex: pivotPos, mergeRegion: [low, high] }),
        highlightedIndices: [pivotPos],
        message: `${pivot} is in final position ${pivotPos} — ${pivotPos < k ? 'target is right' : pivotPos > k ? 'target is left' : 'found!'}`,
        isComparison: false,
        isSwap: true,
      })

      return pivotPos
    }

    function quickselect(low: number, high: number): void {
      if (low >= high) {
        finalized.add(low)
        return
      }
      const pivotPos = partition(low, high)
      if (pivotPos === k) return
      else if (pivotPos < k) quickselect(pivotPos + 1, high)
      else quickselect(low, pivotPos - 1)
    }

    quickselect(0, n - 1)

    finalized.add(k)
    steps.push({
      index: idx++,
      state: state({ minIndex: k }),
      highlightedIndices: [k],
      message: `Found! Score threshold = ${a[k]} — top 25% of users qualify for the campaign. O(n) average.`,
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateHeapSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const arr = shuffle(Array.from({ length: size }, (_, i) => i + 1))
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    let sortedFromIndex = n

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex,
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Ready — press Play to start.',
      isComparison: false,
      isSwap: false,
    })

    function heapify(heapSize: number, root: number): void {
      let largest = root
      const left = 2 * root + 1
      const right = 2 * root + 2

      if (left < heapSize) {
        steps.push({
          index: idx++,
          state: state({ comparing: [largest, left], minIndex: root }),
          highlightedIndices: [largest, left],
          message: `Compare parent ${a[largest]} with left child ${a[left]}`,
          isComparison: true,
          isSwap: false,
        })
        if (a[left] > a[largest]) largest = left
      }

      if (right < heapSize) {
        steps.push({
          index: idx++,
          state: state({ comparing: [largest, right], minIndex: root }),
          highlightedIndices: [largest, right],
          message: `Compare ${a[largest]} with right child ${a[right]}`,
          isComparison: true,
          isSwap: false,
        })
        if (a[right] > a[largest]) largest = right
      }

      if (largest !== root) {
        ;[a[root], a[largest]] = [a[largest], a[root]]
        steps.push({
          index: idx++,
          state: state({ comparing: [root, largest], swapped: true, minIndex: root }),
          highlightedIndices: [root, largest],
          message: `${a[largest]} was larger — sift down: swap with child`,
          isComparison: false,
          isSwap: true,
        })
        heapify(heapSize, largest)
      }
    }

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Phase 1: Build max-heap — largest element rises to root',
      isComparison: false,
      isSwap: false,
    })

    for (let i = Math.floor(n / 2) - 1; i >= 0; i--) {
      heapify(n, i)
    }

    steps.push({
      index: idx++,
      state: state({ minIndex: 0 }),
      highlightedIndices: [0],
      message: `Max-heap ready — ${a[0]} is the root (maximum). Phase 2: extract in order`,
      isComparison: false,
      isSwap: false,
    })

    for (let i = n - 1; i > 0; i--) {
      ;[a[0], a[i]] = [a[i], a[0]]
      sortedFromIndex = i

      steps.push({
        index: idx++,
        state: state({ comparing: [0, i], swapped: true }),
        highlightedIndices: [0, i],
        message: `Extract max ${a[i]} — move to position ${i}, rebuild heap`,
        isComparison: false,
        isSwap: true,
      })

      heapify(i, 0)
    }

    sortedFromIndex = 0
    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Sorted! O(n log n), O(1) space — the heap is a priority queue in disguise.',
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateCountingSortSteps(size: number): VisualizationStep<BarArrayState>[] {
    const maxVal = 10
    const arr = Array.from({ length: size }, () => Math.floor(Math.random() * maxVal) + 1)
    const steps: VisualizationStep<BarArrayState>[] = []
    const a = [...arr]
    const n = a.length
    let idx = 0
    let sortedToIndex = -1

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex: n,
      sortedToIndex,
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: `${n} transactions, account tiers 1–${maxVal}. Press Play to start.`,
      isComparison: false,
      isSwap: false,
    })

    const count = new Array(maxVal + 1).fill(0)

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Phase 1: Count each tier — one pass, zero comparisons',
      isComparison: false,
      isSwap: false,
    })

    for (let i = 0; i < n; i++) {
      count[a[i]]++
      steps.push({
        index: idx++,
        state: state({ minIndex: i }),
        highlightedIndices: [i],
        message: `Tier ${a[i]}: count[${a[i]}] = ${count[a[i]]}`,
        isComparison: false,
        isSwap: false,
      })
    }

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: 'Phase 2: Write back in tier order — read each bucket sequentially',
      isComparison: false,
      isSwap: false,
    })

    let writePos = 0
    for (let v = 1; v <= maxVal; v++) {
      if (count[v] === 0) continue

      steps.push({
        index: idx++,
        state: state(),
        highlightedIndices: [],
        message: `Tier ${v}: ${count[v]} record${count[v] > 1 ? 's' : ''} — placing at position${count[v] > 1 ? 's' : ''} ${writePos}${count[v] > 1 ? '–' + (writePos + count[v] - 1) : ''}`,
        isComparison: false,
        isSwap: false,
      })

      for (let c = 0; c < count[v]; c++) {
        a[writePos] = v
        sortedToIndex = writePos
        steps.push({
          index: idx++,
          state: state({ minIndex: writePos }),
          highlightedIndices: [writePos],
          message: `Placed tier ${v} at position ${writePos}`,
          isComparison: false,
          isSwap: true,
        })
        writePos++
      }
    }

    sortedToIndex = n - 1
    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: `Sorted! O(n + k) — ${n} records, ${maxVal} tiers, zero comparisons. ETL in minutes, not hours.`,
      isComparison: false,
      isSwap: false,
    })

    return steps
  }

  static generateBinarySearchSteps(size: number): VisualizationStep<BarArrayState>[] {
    const a = Array.from({ length: size }, (_, i) => i + 1)
    const target = a[Math.floor(Math.random() * size)]
    const steps: VisualizationStep<BarArrayState>[] = []
    let idx = 0

    const state = (extra: Partial<BarArrayState> = {}): BarArrayState => ({
      array: [...a],
      comparing: null,
      swapped: false,
      sortedFromIndex: size,
      searchRange: [0, size - 1],
      ...extra,
    })

    steps.push({
      index: idx++,
      state: state(),
      highlightedIndices: [],
      message: `Search for ${target} in ${size} sorted records. Linear scan: up to ${size} comparisons.`,
      isComparison: false,
      isSwap: false,
    })

    let left = 0
    let right = size - 1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)

      steps.push({
        index: idx++,
        state: state({ minIndex: mid, searchRange: [left, right] }),
        highlightedIndices: [mid],
        message: `Window [${left}..${right}], mid=${mid}, checking a[${mid}]=${a[mid]} vs target ${target}`,
        isComparison: true,
        isSwap: false,
      })

      if (a[mid] === target) {
        steps.push({
          index: idx++,
          state: {
            ...state({ searchRange: [mid, mid] }),
            finalizedIndices: [mid],
          },
          highlightedIndices: [mid],
          message: `Found ${target} at index ${mid} in ${idx - 2} comparison${idx - 3 === 1 ? '' : 's'}. Linear scan worst case: ${size}.`,
          isComparison: false,
          isSwap: false,
        })
        break
      } else if (a[mid] < target) {
        left = mid + 1
        steps.push({
          index: idx++,
          state: state({ minIndex: mid, searchRange: [left, right] }),
          highlightedIndices: [mid],
          message: `${a[mid]} < ${target} — eliminate left half, search [${left}..${right}]`,
          isComparison: false,
          isSwap: false,
        })
      } else {
        right = mid - 1
        steps.push({
          index: idx++,
          state: state({ minIndex: mid, searchRange: [left, right] }),
          highlightedIndices: [mid],
          message: `${a[mid]} > ${target} — eliminate right half, search [${left}..${right}]`,
          isComparison: false,
          isSwap: false,
        })
      }
    }

    return steps
  }

  render(step: VisualizationStep<BarArrayState>): void {
    const { canvas, ctx } = this
    const { array, comparing, sortedFromIndex, sortedToIndex, minIndex, mergeRegion, pivotIndex, finalizedIndices, searchRange } = step.state
    const n = array.length
    const max = Math.max(...array)
    const W = canvas.width
    const H = canvas.height
    const gap = 2
    const barW = (W - gap * (n + 1)) / n
    const labelH = 20

    ctx.clearRect(0, 0, W, H)

    for (let i = 0; i < n; i++) {
      const barH = (array[i] / max) * (H - labelH - 8)
      const x = gap + i * (barW + gap)
      const y = H - labelH - barH

      const isSorted =
        i >= sortedFromIndex ||
        (sortedToIndex !== undefined && sortedToIndex >= 0 && i <= sortedToIndex) ||
        (finalizedIndices !== undefined && finalizedIndices.includes(i))
      const isComparing = comparing !== null && (comparing[0] === i || comparing[1] === i)
      const isMin = minIndex !== undefined && minIndex === i
      const isInMergeRegion = mergeRegion !== undefined && i >= mergeRegion[0] && i <= mergeRegion[1]
      const isPivot = pivotIndex !== undefined && pivotIndex === i
      const isOutsideSearch = searchRange !== undefined && (i < searchRange[0] || i > searchRange[1])

      if (isSorted) {
        ctx.fillStyle = '#00ff88'
      } else if (isOutsideSearch) {
        ctx.fillStyle = '#1e1e2e'
      } else if (step.isSwap && isComparing) {
        ctx.fillStyle = '#f97316'
      } else if (isPivot) {
        ctx.fillStyle = '#06b6d4'
      } else if (isComparing || isMin) {
        ctx.fillStyle = '#f7c948'
      } else if (isInMergeRegion) {
        ctx.fillStyle = '#5b2fa8'
      } else {
        ctx.fillStyle = '#7c3aed'
      }

      ctx.fillRect(x, y, barW, barH)
    }

    ctx.fillStyle = '#6b7280'
    ctx.font = '11px monospace'
    ctx.fillText(step.message, 6, H - 4)
  }
}
